import { c as _c } from "react/compiler-runtime";
import { IcoRefresh16 } from '@onefootprint/icons';
import type { OnboardingSessionFixtureResult, PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { CodeInline, Form, IconButton, Tooltip } from '@onefootprint/ui';
import { cva } from 'class-variance-authority';
import JSON5 from 'json5';
import Link from 'next/link';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import CollapsibleSection from '@/components/playbook-builder/components/collapsible-section';
import useSession from '@/hooks/use-session';
import type { OrgOnboardingRequestBody } from './test-form.types';
const TEST_EXT_ID_SUFFIX_LEN = 12;
const TEST_EXT_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
export const generateTestExternalId = (): string => {
  let suffix = '';
  for (let i = 0; i < TEST_EXT_ID_SUFFIX_LEN; i += 1) {
    suffix += TEST_EXT_ID_ALPHABET.charAt(Math.floor(Math.random() * TEST_EXT_ID_ALPHABET.length));
  }
  return `test_ext_id_${suffix}`;
};
type TestFormProps = {
  playbook: PlaybookVersionNoGraph;
  onSubmit: (data: OrgOnboardingRequestBody) => Promise<void>;
};
export type TestFormValues = {
  bootstrapData?: string;
  onboardingData?: string;
  fpId?: string;
  userExternalId?: string;
  businessExternalId?: string;
  sandboxId?: string;
  sandboxOutcome?: OnboardingSessionFixtureResult;
  onboardingExternalId?: string;
};
export const TestForm = t0 => {
  const $ = _c(113);
  const {
    playbook,
    onSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "testMode.testForm"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    control,
    handleSubmit,
    setValue,
    getValues
  } = useFormContext();
  const {
    isLive
  } = useSession();
  const requiresAuthentication = playbook.requiresAuthentication;
  let t2;
  let t3;
  if ($[1] !== getValues || $[2] !== requiresAuthentication || $[3] !== setValue) {
    t2 = () => {
      if (!requiresAuthentication) {
        const {
          fpId,
          userExternalId
        } = getValues();
        if (!fpId && !userExternalId) {
          setValue("userExternalId", generateTestExternalId());
        }
      }
    };
    t3 = [requiresAuthentication, getValues, setValue];
    $[1] = getValues;
    $[2] = requiresAuthentication;
    $[3] = setValue;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== setValue) {
    t4 = () => {
      setValue("userExternalId", generateTestExternalId(), {
        shouldDirty: true
      });
    };
    $[6] = setValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRegenerateUserExternalId = t4;
  let t5;
  if ($[8] !== onSubmit) {
    t5 = async data => {
      onSubmit({
        bootstrap_data: data.bootstrapData ? JSON5.parse(data.bootstrapData) : undefined,
        onboarding_data: data.onboardingData ? JSON5.parse(data.onboardingData) : undefined,
        fp_id: data.fpId ? data.fpId : undefined,
        user_external_id: data.userExternalId ? data.userExternalId : undefined,
        business_external_id: data.businessExternalId ? data.businessExternalId : undefined,
        sandbox_id: data.sandboxId ? data.sandboxId : undefined,
        sandbox_outcome: data.sandboxOutcome ? data.sandboxOutcome : undefined,
        onboarding_external_id: data.onboardingExternalId ? data.onboardingExternalId : undefined,
        use_implicit_auth: false
      });
    };
    $[8] = onSubmit;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleFormSubmit = t5;
  let t6;
  if ($[10] !== control) {
    t6 = {
      control,
      name: "fpId"
    };
    $[10] = control;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const fpId_0 = useWatch(t6);
  let t7;
  if ($[12] !== control) {
    t7 = {
      control,
      name: "userExternalId"
    };
    $[12] = control;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const userExternalId_0 = useWatch(t7);
  let t8;
  if ($[14] !== fpId_0 || $[15] !== t) {
    t8 = fpId_0 ? t(_temp) : undefined;
    $[14] = fpId_0;
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t || $[18] !== userExternalId_0) {
    t9 = userExternalId_0 ? t(_temp2) : undefined;
    $[17] = t;
    $[18] = userExternalId_0;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== fpId_0 || $[21] !== t || $[22] !== userExternalId_0) {
    t10 = !fpId_0 && !userExternalId_0 ? t(_temp3) : undefined;
    $[20] = fpId_0;
    $[21] = t;
    $[22] = userExternalId_0;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8 || $[26] !== t9) {
    t11 = {
      userExternalId: t8,
      fpId: t9,
      obExternalId: t10
    };
    $[24] = t10;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const disabledTooltips = t11;
  let t12;
  if ($[28] !== handleFormSubmit || $[29] !== handleSubmit) {
    t12 = handleSubmit(handleFormSubmit);
    $[28] = handleFormSubmit;
    $[29] = handleSubmit;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t) {
    t13 = t(_temp4);
    $[31] = t;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t) {
    t14 = t(_temp6);
    $[33] = t;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t14) {
    t15 = <Trans ns="playbook-builder" i18nKey={_temp5} components={{
      link: <Link href="https://docs.onefootprint.com/articles/integrate/bootstrap-data" target="_blank" rel="noreferrer" className="text-accent hover:underline">{t14}</Link>
    }} />;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t13 || $[38] !== t15) {
    t16 = <SectionHeader title={t13} description={t15} />;
    $[37] = t13;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Form.JsonEditor name="bootstrapData" allowTrailingCommas={true} />;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t) {
    t18 = t(_temp7);
    $[41] = t;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Trans ns="playbook-builder" i18nKey={_temp8} components={{
      codeInline: <CodeInline size="compact" disabled={true}>{"data."}</CodeInline>
    }} />;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t18) {
    t20 = <SectionHeader title={t18} description={t19} />;
    $[44] = t18;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Form.JsonEditor name="onboardingData" size="medium" allowTrailingCommas={true} />;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t) {
    t22 = t(_temp9);
    $[47] = t;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t) {
    t23 = t(_temp10);
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t) {
    t24 = t(_temp11);
    $[51] = t;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t23 || $[54] !== t24) {
    t25 = <SectionHeader title={t23} description={t24} />;
    $[53] = t23;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const t26 = !!disabledTooltips.fpId;
  let t27;
  if ($[56] !== t) {
    t27 = {
      validate: value => {
        if (typeof value === "string" && value.startsWith("fp_bid")) {
          return t(_temp12);
        }
        return true;
      }
    };
    $[56] = t;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== disabledTooltips.fpId || $[59] !== t26 || $[60] !== t27) {
    t28 = <Form.Input name="fpId" placeholder="fp_id_xxx" disabled={t26} hint={disabledTooltips.fpId} rules={t27} />;
    $[58] = disabledTooltips.fpId;
    $[59] = t26;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t) {
    t29 = t(_temp13);
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t) {
    t30 = t(_temp14);
    $[64] = t;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t29 || $[67] !== t30) {
    t31 = <SectionHeader title={t29} description={t30} />;
    $[66] = t29;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  const t32 = !!disabledTooltips.userExternalId;
  let t33;
  if ($[69] !== disabledTooltips.userExternalId || $[70] !== t32) {
    t33 = <div className="grow"><Form.Input name="userExternalId" placeholder="abcde12345" hint={disabledTooltips.userExternalId} disabled={t32} /></div>;
    $[69] = disabledTooltips.userExternalId;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== disabledTooltips.userExternalId || $[73] !== handleRegenerateUserExternalId || $[74] !== requiresAuthentication || $[75] !== t) {
    t34 = !requiresAuthentication && <Tooltip content={t(_temp15)}><IconButton aria-label={t(_temp16)} icon={IcoRefresh16} onClick={handleRegenerateUserExternalId} size="large" variant="outline" disabled={!!disabledTooltips.userExternalId} /></Tooltip>;
    $[72] = disabledTooltips.userExternalId;
    $[73] = handleRegenerateUserExternalId;
    $[74] = requiresAuthentication;
    $[75] = t;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t33 || $[78] !== t34) {
    t35 = <div className="flex items-start gap-2">{t33}{t34}</div>;
    $[77] = t33;
    $[78] = t34;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== playbook.kind || $[81] !== t) {
    t36 = playbook.kind === "kyb" && <><SectionHeader title={t(_temp17)} description={t(_temp18)} /><Form.Input name="businessExternalId" placeholder="abcde12345" /></>;
    $[80] = playbook.kind;
    $[81] = t;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] !== t) {
    t37 = t(_temp19);
    $[83] = t;
    $[84] = t37;
  } else {
    t37 = $[84];
  }
  let t38;
  if ($[85] !== t) {
    t38 = t(_temp21);
    $[85] = t;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  let t39;
  if ($[87] !== t38) {
    t39 = <Trans ns="playbook-builder" i18nKey={_temp20} components={{
      link: <Link href="https://docs.onefootprint.com/articles/integrate/onboarding-sessions#example-use-cases-onboarding-idempotency" target="_blank" rel="noreferrer" className="text-accent hover:underline">{t38}</Link>
    }} />;
    $[87] = t38;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== t37 || $[90] !== t39) {
    t40 = <SectionHeader title={t37} description={t39} />;
    $[89] = t37;
    $[90] = t39;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  const t41 = !!disabledTooltips.obExternalId;
  let t42;
  if ($[92] !== disabledTooltips.obExternalId || $[93] !== t41) {
    t42 = <Form.Input name="onboardingExternalId" placeholder="abcde12345" disabled={t41} hint={disabledTooltips.obExternalId} />;
    $[92] = disabledTooltips.obExternalId;
    $[93] = t41;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  let t43;
  if ($[95] !== t22 || $[96] !== t25 || $[97] !== t28 || $[98] !== t31 || $[99] !== t35 || $[100] !== t36 || $[101] !== t40 || $[102] !== t42) {
    t43 = <CollapsibleSection title={t22} defaultOpen={true} className="flex flex-col gap-4 p-4">{t25}{t28}{t31}{t35}{t36}{t40}{t42}</CollapsibleSection>;
    $[95] = t22;
    $[96] = t25;
    $[97] = t28;
    $[98] = t31;
    $[99] = t35;
    $[100] = t36;
    $[101] = t40;
    $[102] = t42;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  let t44;
  if ($[104] !== isLive || $[105] !== t) {
    t44 = !isLive && <CollapsibleSection title={t(_temp22)} className="flex flex-col gap-4 p-4"><SectionHeader title={t(_temp23)} description={t(_temp24)} /><Form.Input name="sandboxId" placeholder="abcde12345" /><SectionHeader title={t(_temp25)} description={t(_temp26)} /><Form.Select name="sandboxOutcome" options={[{
        value: "pass",
        label: t(_temp27)
      }, {
        value: "fail",
        label: t(_temp28)
      }, {
        value: "manual_review",
        label: t(_temp29)
      }, {
        value: "evaluate_rules",
        label: t(_temp30)
      }, {
        value: "choose_branch_outcomes",
        label: t(_temp31)
      }]} /></CollapsibleSection>;
    $[104] = isLive;
    $[105] = t;
    $[106] = t44;
  } else {
    t44 = $[106];
  }
  let t45;
  if ($[107] !== t12 || $[108] !== t16 || $[109] !== t20 || $[110] !== t43 || $[111] !== t44) {
    t45 = <form id="test-form" onSubmit={t12} className="bg-primary flex flex-col gap-6 px-4 mb-6 mt-4">{t16}{t17}{t20}{t21}{t43}{t44}</form>;
    $[107] = t12;
    $[108] = t16;
    $[109] = t20;
    $[110] = t43;
    $[111] = t44;
    $[112] = t45;
  } else {
    t45 = $[112];
  }
  return t45;
};
const sectionTitle = cva('text-primary', {
  variants: {
    size: {
      default: 'text-label-2',
      compact: 'text-label-3'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});
const sectionDescription = cva('text-tertiary', {
  variants: {
    size: {
      default: 'text-body-2',
      compact: 'text-body-3'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});
type SectionHeaderProps = {
  title: string;
  description: React.ReactNode;
  size?: 'default' | 'compact';
};
export const SectionHeader = t0 => {
  const $ = _c(13);
  const {
    title,
    description,
    size
  } = t0;
  let t1;
  if ($[0] !== size) {
    t1 = sectionTitle({
      size
    });
    $[0] = size;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== title) {
    t2 = <p className={t1}>{title}</p>;
    $[2] = t1;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== size) {
    t3 = sectionDescription({
      size
    });
    $[5] = size;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== description || $[8] !== t3) {
    t4 = <p className={t3}>{description}</p>;
    $[7] = description;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className="flex flex-col gap-1">{t2}{t4}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
function _temp($) {
  return $.tooltip.userExternalIdDisabled;
}
function _temp2($_0) {
  return $_0.tooltip.fpIdDisabled;
}
function _temp3($_1) {
  return $_1.tooltip.obExternalIdDisabled;
}
function _temp4($_2) {
  return $_2.fields.bootstrapData;
}
function _temp5($_3) {
  return $_3.testMode.testForm.documentation.bootstrapData;
}
function _temp6($_4) {
  return $_4.documentation.here;
}
function _temp7($_5) {
  return $_5.fields.onboardingData;
}
function _temp8($_6) {
  return $_6.testMode.testForm.documentation.onboardingData;
}
function _temp9($_7) {
  return $_7.fields.idempotencyControl;
}
function _temp10($_8) {
  return $_8.fields.fpId;
}
function _temp11($_9) {
  return $_9.documentation.fpId;
}
function _temp12($_10) {
  return $_10.mustBeUserFpId;
}
function _temp13($_11) {
  return $_11.fields.userExternalId;
}
function _temp14($_12) {
  return $_12.documentation.userExternalId;
}
function _temp15($_13) {
  return $_13.regenerateUserExternalId;
}
function _temp16($_14) {
  return $_14.regenerateUserExternalId;
}
function _temp17($_15) {
  return $_15.fields.bizExternalId;
}
function _temp18($_16) {
  return $_16.documentation.bizExternalId;
}
function _temp19($_17) {
  return $_17.fields.obExternalId;
}
function _temp20($_18) {
  return $_18.testMode.testForm.documentation.obExternalId;
}
function _temp21($_19) {
  return $_19.documentation.here;
}
function _temp22($_20) {
  return $_20.fields.sandboxModeSettings;
}
function _temp23($_21) {
  return $_21.fields.sandboxId;
}
function _temp24($_22) {
  return $_22.documentation.sandboxId;
}
function _temp25($_23) {
  return $_23.fields.sandboxOutcome;
}
function _temp26($_24) {
  return $_24.documentation.sandboxOutcome;
}
function _temp27($_25) {
  return $_25.documentation.sandboxOutcomes.pass;
}
function _temp28($_26) {
  return $_26.documentation.sandboxOutcomes.fail;
}
function _temp29($_27) {
  return $_27.documentation.sandboxOutcomes.manualReview;
}
function _temp30($_28) {
  return $_28.documentation.sandboxOutcomes.evaluateRules;
}
function _temp31($_29) {
  return $_29.documentation.sandboxOutcomes.chooseBranchOutcomes;
}